import { useRef, useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

const fleetBrand = {
  teal: "#4A9B8E",
  tealLight: "#5AADA0",
  aqua: "#82C5BB",
};

interface FleetLoginDropdownProps {
  onForgotPassword?: () => void;
  onSignup?: () => void;
  triggerButton?: React.ReactNode;
}

export function FleetLoginDropdown({ 
  onForgotPassword, 
  onSignup, 
  triggerButton 
}: FleetLoginDropdownProps) {
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setTimeout(() => emailRef.current?.focus(), 100);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const email = emailRef.current?.value || '';
    const password = passwordRef.current?.value || '';
    
    if (!email || !password) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Preencha email e senha",
      });
      return;
    }
    
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      
      if (error) {
        toast({
          variant: "destructive",
          title: "Erro ao fazer login",
          description: error.message === "Invalid login credentials" 
            ? "Email ou senha incorretos" 
            : error.message,
        });
        return;
      }

      toast({
        title: "Login realizado!",
        description: "Redirecionando...",
      });
      
      setOpen(false);
      navigate("/");
      
      if (emailRef.current) emailRef.current.value = '';
      if (passwordRef.current) passwordRef.current.value = '';
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Ocorreu um erro ao fazer login",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {triggerButton || (
          <button className="px-4 py-2 rounded-md text-white font-medium">
            Login
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
          <div className="space-y-2">
            <h3 
              className="font-semibold text-lg"
              style={{ color: fleetBrand.teal }}
            >
              Login - FleetONE
            </h3>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fleet-dropdown-email">Email</Label>
            <Input
              ref={emailRef}
              id="fleet-dropdown-email"
              name="email"
              type="email"
              placeholder="seu@email.com"
              defaultValue=""
              autoComplete="username"
              inputMode="email"
              required
              disabled={loading}
              className="focus-visible:ring-[#4A9B8E] focus-visible:border-[#4A9B8E]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fleet-dropdown-password">Senha</Label>
            <Input
              ref={passwordRef}
              id="fleet-dropdown-password"
              name="password"
              type="password"
              placeholder="••••••"
              defaultValue=""
              autoComplete="current-password"
              required
              disabled={loading}
              className="focus-visible:ring-[#4A9B8E] focus-visible:border-[#4A9B8E]"
            />
          </div>

          {onForgotPassword && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onForgotPassword();
              }}
              className="text-sm hover:underline p-0 h-auto font-normal"
              style={{ color: fleetBrand.teal }}
            >
              Esqueceu sua senha?
            </button>
          )}

          <button 
            type="submit" 
            disabled={loading}
            className="w-full py-2 px-4 rounded-md text-white font-medium transition-all disabled:opacity-50 disabled:pointer-events-none"
            style={{
              background: loading 
                ? '#94a3b8'
                : `linear-gradient(90deg, ${fleetBrand.teal}, ${fleetBrand.aqua})`,
              boxShadow: loading 
                ? 'none' 
                : `0 0 20px ${fleetBrand.teal}33`,
            }}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Entrando...
              </span>
            ) : (
              "Entrar"
            )}
          </button>

          {onSignup && (
            <div className="text-center text-sm text-muted-foreground">
              Não tem conta?{" "}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSignup();
                }}
                className="hover:underline font-medium"
                style={{ color: fleetBrand.teal }}
              >
                Criar conta
              </button>
            </div>
          )}
        </form>
      </PopoverContent>
    </Popover>
  );
}
